import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import ConfigAPI from '../services/configApi';
import { signOut } from '../features/authSlice';

export default function SettingsModal({ onComplete, onCancel }) {
  const dispatch = useDispatch();
  const configCarouselRef = useRef(null);
  const brandCarouselRef = useRef(null);
  const videoRefs = useRef({});
  
  // Get current brand from Redux state
  const currentBrand = useSelector((state) => state.channel.currentBrand);
  // Light/dark theme for surfaces styled inline (CSS class overrides can't reach them).
  const isLightTheme = useSelector((state) => state.device.theme) === 'light';
  // Also get customerId which stores the brand ID
  const customerId = useSelector((state) => state.channel.customerId);

  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true); // Start with loading true
  const [error, setError] = useState(null);
  const [applying, setApplying] = useState(false);
  const [centerConfigIndex, setCenterConfigIndex] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [hasChanges, setHasChanges] = useState(false);
  // Cache for fully-loaded config details (keyed by config ID), fetched on tile click
  const [configDetails, setConfigDetails] = useState({});

  const normalizeData = (items, type) => {
    if (!Array.isArray(items)) return [];
    const seen = new Set();
    return items.map((item, index) => {
      // Ensure we have a valid ID (not null, undefined, or empty string)
      let id = item.id ?? item._id;
      if (id === null || id === undefined || id === '') {
        id = `${type}_${index}`;
        console.warn(`⚠️ ${type} at index ${index} has no valid ID, using generated ID: ${id}`);
      }
      
      // Ensure uniqueness
      let uniqueId = id;
      let suffix = 0;
      while (seen.has(String(uniqueId))) {
        suffix++;
        uniqueId = `${id}_dup${suffix}`;
        console.warn(`⚠️ Duplicate ${type} ID detected: ${id}, using ${uniqueId}`);
      }
      seen.add(String(uniqueId));
      
      let name = item.name;
      if (!name) {
        name = item.config_json?.name || item.brand_json?.name || item.hostServer || `${type} ${uniqueId}`;
      }
      return { ...item, id: uniqueId, name };
    });
  };

  useEffect(() => {
    const loadOptions = async () => {
      try {
        setLoading(true);
        setError(null);
        const [configsData, brandsData] = await Promise.all([
          ConfigAPI.getConfigurations(),
          ConfigAPI.getBrands()
        ]);
        let normalizedConfigs = normalizeData(configsData, 'Configuration');
        let normalizedBrands = normalizeData(brandsData, 'Brand');
        
        // /configurations endpoint should return description and onboardingEvents directly.
        // If configs are missing these fields, update the backend /configurations endpoint
        // to include them rather than re-fetching each config individually.
        console.log('✅ Configs loaded:', normalizedConfigs.length);
        
        // /brands endpoint now returns logoUrl directly — no per-brand fetch needed
        console.log('✅ Brands loaded:', normalizedBrands.length);
        
        // Debug: Log data structure and check for duplicates
        console.log('📊 Loaded configurations:', {
          total: normalizedConfigs.length,
          ids: normalizedConfigs.map(c => c.id),
          uniqueIds: new Set(normalizedConfigs.map(c => c.id)).size
        });
        console.log('📊 Loaded brands:', {
          total: normalizedBrands.length,
          ids: normalizedBrands.map(b => b.id),
          uniqueIds: new Set(normalizedBrands.map(b => b.id)).size
        });
        
        // Debug: Log first brand to see structure
        if (normalizedBrands.length > 0) {
          console.log('📦 Sample brand structure:', {
            name: normalizedBrands[0].name,
            id: normalizedBrands[0].id,
            hasBrandJson: !!normalizedBrands[0].brand_json,
            hasChannelBranding: !!normalizedBrands[0].brand_json?.channelBranding,
            hasAgentChannel: !!normalizedBrands[0].brand_json?.channelBranding?.agent,
            agentColor: normalizedBrands[0].brand_json?.channelBranding?.agent?.color,
            agentAvatar: normalizedBrands[0].brand_json?.channelBranding?.agent?.avatar,
            topLevelKeys: Object.keys(normalizedBrands[0]),
            brandJsonKeys: normalizedBrands[0].brand_json ? Object.keys(normalizedBrands[0].brand_json) : null
          });
        }
        
        setConfigs(normalizedConfigs);
        setBrands(normalizedBrands);

        // Load from localStorage to sync with currently loaded config/brand
        const savedConfigId = localStorage.getItem('selectedConfigId');
        const savedConfigIdParsed = savedConfigId ? (isNaN(savedConfigId) ? savedConfigId : parseInt(savedConfigId)) : null;
        
        // CRITICAL FIX: Use Redux state as the ONLY source of truth for current brand
        // BrandSwitcher updates Redux immediately, so SettingsModal should read from there
        // Fallback order: currentBrand.id (primary) -> customerId -> localStorage (last resort)
        const currentBrandId = currentBrand?.id ?? customerId;
        const savedBrandIdParsed = currentBrandId;

        console.log('🔍 SettingsModal: Loading brand selection FROM REDUX', {
          'currentBrand.id': currentBrand?.id,
          customerId,
          'selectedBrandId (will use)': savedBrandIdParsed,
          availableBrandIds: normalizedBrands.map(b => ({ id: b.id, name: b.name }))
        });

        // Set selected config and find its index for carousel centering
        if (savedConfigIdParsed !== null) {
          const configIndex = normalizedConfigs.findIndex(c => c.id === savedConfigIdParsed);
          if (configIndex !== -1) {
            setSelectedConfigId(savedConfigIdParsed);
            setCenterConfigIndex(configIndex);
          }
        }
        
        // Set selected brand to match currently loaded brand from Redux
        if (savedBrandIdParsed !== null) {
          const matchingBrand = normalizedBrands.find(b => {
            // Try both strict and loose comparison to handle string/number mismatch
            return b.id === savedBrandIdParsed || 
                   String(b.id) === String(savedBrandIdParsed) ||
                   Number(b.id) === Number(savedBrandIdParsed);
          });
          
          if (matchingBrand) {
            console.log('✅ SettingsModal: Setting selected brand:', matchingBrand.name, matchingBrand.id);
            console.log('✅ SettingsModal: Calling setSelectedBrandId with:', matchingBrand.id);
            setSelectedBrandId(matchingBrand.id);
            console.log('✅ SettingsModal: setSelectedBrandId called');
          } else {
            console.warn('⚠️ SettingsModal: No matching brand found for ID:', savedBrandIdParsed);
            console.warn('⚠️ SettingsModal: Available brand IDs:', normalizedBrands.map(b => `${b.id} (${typeof b.id})`));
            console.warn('⚠️ SettingsModal: Looking for:', savedBrandIdParsed, '(', typeof savedBrandIdParsed, ')');
          }
        } else {
          console.warn('⚠️ SettingsModal: No current brand ID available');
        }
        
        // No changes yet - user just opened the modal
        setHasChanges(false);
      } catch (err) {
        console.error('❌ Failed to load configurations:', err);
        setError('Failed to load available configurations. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadOptions();
  }, [currentBrand, customerId]); // Add customerId as dependency so modal re-initializes when brand ID changes

  // Cleanup video refs on unmount to prevent memory leaks and null reference errors
  useEffect(() => {
    return () => {
      // Clean up all video refs
      Object.keys(videoRefs.current).forEach(key => {
        const video = videoRefs.current[key];
        if (video) {
          video.pause();
          video.src = '';
          video.load();
        }
      });
      videoRefs.current = {};
    };
  }, []);

  // Auto-load config details and play video for initially selected config when modal opens
  useEffect(() => {
    if (selectedConfigId && configs.length > 0 && !loading) {
      const selectedConfig = configs.find(c => c.id === selectedConfigId);
      if (selectedConfig && !configDetails[selectedConfigId]) {
        // Load config details if not already loaded
        const loadConfigDetails = async () => {
          try {
            console.log(`🔍 Auto-loading config details for initially selected config: ${selectedConfig.name || selectedConfigId}`);
            const detail = await ConfigAPI.getConfiguration(selectedConfigId);
            setConfigDetails(prev => ({ ...prev, [selectedConfigId]: detail }));
            console.log(`✅ Auto-loaded config detail:`, detail);
            
            // Check if this config has a video and try to play it
            const configData = detail?.config_json || detail || selectedConfig.config_json || selectedConfig;
            if (configData.onboardingEvents && Array.isArray(configData.onboardingEvents)) {
              const videoEvent = configData.onboardingEvents.find(event => event.type === 'video' && event.file);
              if (videoEvent) {
                console.log(`🎬 Auto-playing video for selected config: ${videoEvent.file}`);
                // Small delay to ensure video element is rendered
                setTimeout(() => {
                  const video = videoRefs.current[selectedConfigId];
                  if (video) {
                    video.play().catch(err => console.log('Auto-play prevented:', err));
                  }
                }, 100);
              }
            }
          } catch (err) {
            console.error(`❌ Failed to auto-load config details:`, err);
          }
        };
        loadConfigDetails();
      } else if (selectedConfig && configDetails[selectedConfigId]) {
        // Config details already loaded, just try to play video
        const configData = configDetails[selectedConfigId]?.config_json || configDetails[selectedConfigId] || selectedConfig.config_json || selectedConfig;
        if (configData.onboardingEvents && Array.isArray(configData.onboardingEvents)) {
          const videoEvent = configData.onboardingEvents.find(event => event.type === 'video' && event.file);
          if (videoEvent) {
            console.log(`🎬 Auto-playing video for pre-loaded config: ${videoEvent.file}`);
            // Small delay to ensure video element is rendered
            setTimeout(() => {
              const video = videoRefs.current[selectedConfigId];
              if (video) {
                video.play().catch(err => console.log('Auto-play prevented:', err));
              }
            }, 100);
          }
        }
      }
    }
  }, [selectedConfigId, configs, configDetails, loading]);

  const handleLoad = async () => {
    if (!selectedConfigId || !selectedBrandId) return;
    
    // Check if user made any changes
    const savedConfigId = localStorage.getItem('selectedConfigId');
    const savedBrandId = localStorage.getItem('selectedBrandId');
    const savedConfigIdParsed = savedConfigId ? (isNaN(savedConfigId) ? savedConfigId : parseInt(savedConfigId)) : null;
    const savedBrandIdParsed = savedBrandId ? (isNaN(savedBrandId) ? savedBrandId : parseInt(savedBrandId)) : null;
    
    const configChanged = selectedConfigId !== savedConfigIdParsed;
    const brandChanged = selectedBrandId !== savedBrandIdParsed;
    
    if (!configChanged && !brandChanged) {
      // No changes, just close the modal
      console.log('⚙️ SettingsModal: No changes detected, closing without reload');
      if (onCancel) {
        onCancel();
      } else if (onComplete) {
        onComplete(false); // Pass false to indicate no changes
      }
      return;
    }
    
    // Show loading overlay while config loads in background
    console.log('⚙️ SettingsModal: Starting config load with loading overlay...');
    setApplying(true);
    
    // Store the config/brand IDs to load
    const configToLoad = selectedConfigId;
    const brandToLoad = selectedBrandId;
    
    // Call onComplete which will handle the async loading
    // Modal will stay open with loading overlay until parent calls closeModal callback
    if (onComplete) {
      await onComplete(true, configToLoad, brandToLoad);
    }
  };
  
  const handleClose = () => {
    console.log('⚙️ SettingsModal: Close button clicked');
    if (onCancel) {
      onCancel();
    } else if (onComplete) {
      onComplete(false); // Pass false to indicate modal was closed without changes
    }
  };

  const handleSignOut = () => {
    if (window.confirm("Are you sure you want to sign out?")) {
      dispatch(signOut());
    }
  };

  const scrollConfigCarousel = (direction) => {
    if (configCarouselRef.current) {
      const scrollAmount = 350;
      configCarouselRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const scrollBrandCarousel = (direction) => {
    if (brandCarouselRef.current) {
      const scrollAmount = 200;
      brandCarouselRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const getBrandDisplay = (brand) => {
    if (!brand) return '';
    return brand.name || `Brand ${brand.id}`;
  };

  // Resolve the primary colour for a brand (ring, active state, etc.)
  // Prefers direct brandColour field from /brands API, falls back to parsing nested structures.
  const getBrandColor = (brand) => {
    if (!brand) return '#44C2FD';
    
    // 1. Primary: Use brandColour from /brands API response
    if (brand.brandColour) return brand.brandColour;
    
    // 2. Fallback: Top-level colors from list endpoint (no brand_json needed)
    if (brand.colors?.gradientColor1) return brand.colors.gradientColor1;
    if (brand.colors?.gradient1) return brand.colors.gradient1;
    if (brand.channelBranding?.agent?.color) return brand.channelBranding.agent.color;
    if (brand.channelBranding?.agent?.gradientColor1) return brand.channelBranding.agent.gradientColor1;

    // 3. brand_json fallback (populated when a brand is fully loaded/applied)
    let brandJson = brand.brand_json;
    if (typeof brandJson === 'string') {
      try { brandJson = JSON.parse(brandJson); } catch (e) { brandJson = null; }
    }
    if (brandJson?.channelBranding?.agent?.color) return brandJson.channelBranding.agent.color;
    if (brandJson?.channelBranding?.agent?.gradientColor1) return brandJson.channelBranding.agent.gradientColor1;
    if (brandJson?.colors?.gradientColor1) return brandJson.colors.gradientColor1;
    if (brandJson?.colors?.gradient1) return brandJson.colors.gradient1;
    
    return '#44C2FD';
  };

  const getBrandAvatar = (brand) => {
    if (!brand) return null;
    
    // Try to parse brand_json if it exists as a string
    let brandJson = brand.brand_json;
    if (typeof brandJson === 'string') {
      try {
        brandJson = JSON.parse(brandJson);
      } catch (e) {
        console.warn(`Failed to parse brand_json for ${brand.name}:`, e);
        brandJson = null;
      }
    }
    
    // 1. Explicit agent avatar
    if (brandJson?.channelBranding?.agent?.avatar) {
      return brandJson.channelBranding.agent.avatar;
    }
    
    // 2. Top-level logoUrl — canonical key
    if (brand.logoUrl) {
      console.log(`🎭 Using logoUrl for ${brand.name}:`, brand.logoUrl);
      return brand.logoUrl;
    }

    // 3. logoUrl inside brand_json
    if (brandJson?.logoUrl) return brandJson.logoUrl;

    console.log(`🎭 No avatar found for ${brand.name}`);
    return null;
  };

  const canLoad = selectedConfigId && selectedBrandId && !applying;

  if (error && !loading && configs.length === 0 && brands.length === 0) {
    return (
      <>
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10000]" />
        <div className="fixed inset-0 flex items-center justify-center z-[10001] p-4 pointer-events-none cara-chrome" style={{ fontFamily: 'Lato, sans-serif' }}>
          <div className="bg-[#1C2837] rounded-lg shadow-2xl p-8 max-w-md text-center pointer-events-auto">
            <div className="text-red-400 text-6xl mb-4">⚠️</div>
            <h2 className="text-white text-2xl font-bold mb-3">CONNECTION ERROR</h2>
            <p className="text-gray-300 mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#3AB0E8] uppercase"
              style={{ background: '#44C2FD', letterSpacing: '0.05em' }}
            >
              RETRY
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <motion.div
      className="fixed inset-0 z-[10000] cara-chrome"
      style={{ fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      {/* Backdrop with blur */}
      <motion.div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      />

      {/* Modal slides in from right - Full width */}
      <motion.div
        className="absolute right-0 top-0 bottom-0 w-full bg-[#1C2837] overflow-y-auto"
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%', opacity: 0 }}
        transition={{ 
          type: 'spring', 
          damping: 25, 
          stiffness: 200,
          exit: { duration: 0.15, ease: 'easeIn' }
        }}
      >
          <div className="h-full flex flex-col"
        >
          {/* Header with enhanced styling */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-700/50 bg-gradient-to-r from-[#141D2B] via-[#1C2837] to-[#141D2B] relative overflow-hidden flex-shrink-0">
            {/* Subtle animated shine effect */}
            <div 
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"
              style={{
                animation: 'shine 3s ease-in-out infinite',
                transform: 'translateX(-100%)',
              }}
            />
            <style>{`
              @keyframes shine {
                0%, 100% { transform: translateX(-100%); }
                50% { transform: translateX(100%); }
              }
            `}</style>
            
            <div className="flex items-center gap-3 relative z-10">
              <div>
                <h2 className="text-xl font-semibold text-white">Stories</h2>
                <p className="text-gray-400 text-sm">
                  {loading 
                    ? 'Loading stories...' 
                    : configs.length === 0 
                      ? 'No stories available, please contact support@ask-cara.com to request access.' 
                      : 'Select a story and apply a brand'
                  }
                </p>
              </div>
            </div>
            {!loading && configs.length > 0 && (
              <div className="flex items-center gap-2 relative z-10">
                <button 
                  onClick={handleClose} 
                  className="text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-full p-2 transition-all duration-200 hover:rotate-90"
                >
                  <span className="material-icons">close</span>
                </button>
              </div>
            )}
          </div>

          {/* Horizontal Story Carousel Section with Scaling */}
          <div className="flex-1 overflow-hidden relative flex flex-col">
            {/* Subtle animated background pattern */}
            <div className="absolute inset-0 opacity-5 pointer-events-none">
              <div className="absolute top-0 left-0 w-72 h-72 bg-[#44C2FD] rounded-full filter blur-3xl animate-pulse"></div>
              <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>

            {/* Story section - no header */}
            <div className="px-8 pt-3 pb-1">
              {/* Headers removed per user request */}
            </div>
            {loading ? (
              <div className="text-center py-12">
                <div className="flex flex-col items-center gap-4">
                  <div className="animate-spin rounded-full h-16 w-16 border-t-3 border-b-3 border-[#44C2FD]"></div>
                  <p className="text-gray-300 font-medium text-lg">Loading stories...</p>
                </div>
              </div>
            ) : configs.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-[#44C2FD]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="material-icons text-[#44C2FD] text-3xl">description</span>
                </div>
                <p className="text-gray-400 mb-2">No stories available, please contact{' '}
                  <a 
                    href="mailto:support@ask-cara.com" 
                    className="text-[#44C2FD] hover:text-[#3bb0eb] transition-colors"
                  >
                    support@ask-cara.com
                  </a>
                  {' '}to request access.
                </p>
              </div>
            ) : (
              <div className="relative px-4 sm:px-8 pb-4 flex-1 flex flex-col">
                {/* Left scroll button */}
                <button
                  onClick={() => scrollConfigCarousel('left')}
                  className="absolute left-0 sm:left-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 sm:w-10 sm:h-10 bg-[#1C2837]/95 hover:bg-[#44C2FD]/20 border border-gray-700 hover:border-[#44C2FD] rounded-full flex items-center justify-center transition-all duration-200 shadow-lg"
                >
                  <span className="material-icons text-sm sm:text-base text-gray-400 hover:text-[#44C2FD]">chevron_left</span>
                </button>

                {/* Horizontal scrolling carousel */}
                <div 
                  ref={configCarouselRef}
                  className="flex gap-3 sm:gap-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory px-6 sm:px-12 py-2 flex-1"
                  style={{ 
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    scrollPaddingLeft: '50%',
                    scrollPaddingRight: '50%'
                  }}
                >
                {configs.map((config, index) => {
                      // Use the fully-loaded detail (fetched on click) if available, else fall back to list data
                      const fetchedDetail = configDetails[config.id];
                      const configData = fetchedDetail?.config_json || fetchedDetail || config.config_json || config;
                      const displayName = config.name || configData.name || `Story ${config.id}`;
                      // Always get the default description from config as a backup
                      const defaultDescription = configData.description || config.description || '';
                      let description = defaultDescription;
                      const isSelected = selectedConfigId === config.id;
                      
                      // Create a guaranteed unique key using multiple fallbacks
                      const uniqueKey = `config-${String(config.id)}-idx${index}-${config.name?.replace(/\s+/g, '-') || 'unnamed'}`;
                      
                      // Debug: Log config structure to understand the data
                      console.log(`📋 Config "${displayName}" structure:`, {
                        hasConfigJson: !!config.config_json,
                        hasOnboardingEvents: !!configData.onboardingEvents,
                        onboardingEventsLength: configData.onboardingEvents?.length,
                        defaultDescription,
                        configKeys: Object.keys(configData),
                        fullConfigData: configData
                      });
                      
                      // Extract video URL and description from onboardingEvents array
                      let videoUrl = null;
                      if (configData.onboardingEvents && Array.isArray(configData.onboardingEvents)) {
                        console.log(`🎬 Checking onboardingEvents for "${displayName}":`, configData.onboardingEvents);
                        const videoEvent = configData.onboardingEvents.find(event => event.type === 'video' && event.file);
                        if (videoEvent) {
                          videoUrl = videoEvent.file;
                          console.log(`✅ Video found for "${displayName}":`, videoUrl);
                          // Prefer video event description, but fall back to default config description
                          description = videoEvent.description || defaultDescription;
                        } else {
                          console.log(`❌ No video event found in onboardingEvents for "${displayName}"`);
                        }
                      } else {
                        console.log(`❌ No onboardingEvents array for "${displayName}"`);
                      }

                      // Generate a unique gradient for each card based on index
                      const gradients = [
                        'from-blue-600/20 via-cyan-500/10 to-transparent',
                        'from-purple-600/20 via-pink-500/10 to-transparent',
                        'from-green-600/20 via-emerald-500/10 to-transparent',
                        'from-orange-600/20 via-yellow-500/10 to-transparent',
                        'from-rose-600/20 via-red-500/10 to-transparent',
                        'from-indigo-600/20 via-blue-500/10 to-transparent',
                      ];
                      const gradient = gradients[index % gradients.length];

                      return (
                        <motion.button
                          key={uniqueKey}
                          onClick={async () => {
                            const savedConfigId = localStorage.getItem('selectedConfigId');
                            const savedConfigIdParsed = savedConfigId ? (isNaN(savedConfigId) ? savedConfigId : parseInt(savedConfigId)) : null;
                            if (config.id !== savedConfigIdParsed) {
                              setHasChanges(true);
                            }
                            setSelectedConfigId(config.id);
                            setCenterConfigIndex(index);
                            // Fetch full config details on first click (lazy, cached)
                            if (!configDetails[config.id]) {
                              try {
                                console.log(`🔍 Fetching full config for "${displayName}" (id: ${config.id})`);
                                const detail = await ConfigAPI.getConfiguration(config.id);
                                setConfigDetails(prev => ({ ...prev, [config.id]: detail }));
                                console.log(`✅ Config detail loaded for "${displayName}":`, detail);
                              } catch (err) {
                                console.error(`❌ Failed to fetch config detail for "${displayName}":`, err);
                              }
                            }
                          }}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ 
                            opacity: 1, 
                            scale: 1,
                          }}
                          transition={{ 
                            duration: 0.5, 
                            delay: index * 0.05,
                            ease: [0.25, 0.46, 0.45, 0.94]
                          }}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className={`relative flex-shrink-0 rounded-2xl text-left transition-all group snap-center ${
                            isSelected 
                              ? 'ring-2 ring-[#44C2FD] shadow-2xl shadow-[#44C2FD]/40' 
                              : 'hover:shadow-xl'
                          }`}
                          style={{ 
                            width: 'clamp(240px, 80vw, 320px)',
                            height: '100%',
                            opacity: Math.abs(centerConfigIndex - index) > 2 ? 0.3 : 1,
                            filter: Math.abs(centerConfigIndex - index) > 1 ? 'blur(1px)' : 'none',
                            overflow: 'hidden',
                            position: 'relative'
                          }}
                        >
                          {/* Video Background (if available) - Absolutely positioned at the back */}
                          {videoUrl && isSelected && (
                            <div className="absolute inset-0 rounded-2xl overflow-hidden" style={{ zIndex: 0 }}>
                              <video
                                ref={(el) => {
                                  if (el) {
                                    videoRefs.current[config.id] = el;
                                  }
                                }}
                                key={videoUrl}
                                className="absolute inset-0 w-full h-full object-cover"
                                autoPlay
                                loop
                                muted
                                playsInline
                                preload="auto"
                                crossOrigin="anonymous"
                                style={{ opacity: 0.6 }}
                                onLoadedData={(e) => {
                                  if (e?.target) {
                                    console.log(`🎥 Video loaded for "${displayName}":`, videoUrl);
                                    e.target.play().catch(err => console.log('Video autoplay prevented:', err));
                                  }
                                }}
                                onError={(e) => {
                                  if (e?.target) {
                                    console.error(`❌ Video failed to load for "${displayName}":`, videoUrl, e);
                                  }
                                }}
                              >
                                <source src={videoUrl} type="video/mp4" />
                              </video>
                            </div>
                          )}
                          
                          {/* Gradient Background Overlay with smoother transitions */}
                          <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${gradient} transition-opacity duration-300 z-[1]`} style={{ opacity: videoUrl && isSelected ? 0.1 : 0.5 }}></div>
                          
                          {/* Glassmorphism Card Background with enhanced blur */}
                          <div className={`absolute inset-0 rounded-2xl transition-all duration-300 z-[2]`} style={{ backgroundColor: isLightTheme ? (videoUrl && isSelected ? 'rgba(255, 255, 255, 0.35)' : 'rgba(255, 255, 255, 0.92)') : (videoUrl && isSelected ? 'rgba(20, 29, 43, 0.3)' : 'rgba(20, 29, 43, 0.9)'), backdropFilter: videoUrl && isSelected ? 'blur(4px)' : 'blur(8px)' }}></div>
                          
                          {/* Subtle inner glow on hover */}
                          <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-[#44C2FD]/0 via-transparent to-transparent group-hover:from-[#44C2FD]/10 transition-all duration-500"></div>
                          
                          {/* Enhanced Animated Border Glow with Pulse */}
                          {isSelected && (
                            <>
                              <motion.div
                                className="absolute inset-0 rounded-2xl"
                                animate={{
                                  boxShadow: [
                                    '0 0 20px rgba(68, 194, 253, 0.4), inset 0 0 20px rgba(68, 194, 253, 0.1)',
                                    '0 0 50px rgba(68, 194, 253, 0.6), inset 0 0 30px rgba(68, 194, 253, 0.2)',
                                    '0 0 20px rgba(68, 194, 253, 0.4), inset 0 0 20px rgba(68, 194, 253, 0.1)',
                                  ],
                                }}
                                transition={{
                                  duration: 2.5,
                                  repeat: Infinity,
                                  ease: 'easeInOut',
                                }}
                              />
                              {/* Additional outer glow ring */}
                              <motion.div
                                className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-[#44C2FD] via-[#23A6D5] to-[#44C2FD] opacity-20 blur-md"
                                animate={{
                                  opacity: [0.2, 0.4, 0.2],
                                }}
                                transition={{
                                  duration: 2.5,
                                  repeat: Infinity,
                                  ease: 'easeInOut',
                                }}
                              />
                            </>
                          )}

                          {/* Card Content */}
                          <div className="relative p-4 sm:p-6 flex flex-col h-full rounded-2xl z-10">
                            {/* Selection Indicator - Absolutely positioned at bottom right */}
                            {isSelected && (
                              <motion.div
                                initial={{ scale: 0, rotate: -180 }}
                                animate={{ scale: 1, rotate: 0 }}
                                className="absolute bottom-4 sm:bottom-6 right-4 sm:right-6 w-7 h-7 sm:w-8 sm:h-8 bg-[#44C2FD] rounded-full flex items-center justify-center shadow-lg z-20"
                              >
                                <span className="material-icons text-white text-base sm:text-lg">check</span>
                              </motion.div>
                            )}

                            {/* Title */}
                            <h4 className={`font-bold text-lg sm:text-xl mb-2 sm:mb-3 line-clamp-2 transition-colors ${
                              isSelected ? 'text-[#44C2FD]' : 'text-white group-hover:text-[#44C2FD]'
                            }`}>
                              {displayName}
                            </h4>

                            {/* Description */}
                            {description && (
                              <p className="text-gray-400 text-xs sm:text-sm line-clamp-6 flex-1 leading-relaxed">
                                {description}
                              </p>
                            )}
                          </div>
                        </motion.button>
                      );
                    })}
                  </div>

                {/* Right scroll button */}
                <button
                  onClick={() => scrollConfigCarousel('right')}
                  className="absolute right-0 sm:right-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 sm:w-10 sm:h-10 bg-[#1C2837]/95 hover:bg-[#44C2FD]/20 border border-gray-700 hover:border-[#44C2FD] rounded-full flex items-center justify-center transition-all duration-200 shadow-lg"
                >
                  <span className="material-icons text-sm sm:text-base text-gray-400 hover:text-[#44C2FD]">chevron_right</span>
                </button>
              </div>
            )}
          </div>

          {/* Brand Carousel Section - Small orbs below story */}
          <div className="px-8 pt-3 pb-3 border-t border-gray-700/30 bg-[#141D2B] flex-shrink-0" style={{ backgroundColor: isLightTheme ? '#f3f5f7' : undefined }}>
            {/* Brand section - no header */}
            <div className="mb-2">
              {/* Headers removed per user request */}
            </div>
            <div className="relative" style={{ height: '110px' }}>
              {/* Left scroll button */}
              {brands.length > 6 && (
                <button
                  onClick={() => scrollBrandCarousel('left')}
                  className="absolute -left-[19px] sm:-left-[27px] top-1/2 -translate-y-1/2 z-10 w-8 h-8 sm:w-10 sm:h-10 bg-[#1C2837]/95 hover:bg-[#44C2FD]/20 border border-gray-700 hover:border-[#44C2FD] rounded-full flex items-center justify-center transition-all duration-200 shadow-lg"
                >
                  <span className="material-icons text-sm sm:text-base text-gray-400 hover:text-[#44C2FD]">chevron_left</span>
                </button>
              )}

              {/* Brand orbs carousel */}
              <div 
                ref={brandCarouselRef}
                className="flex items-start gap-4 overflow-x-auto scrollbar-hide px-10 h-full pt-2"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {brands.map((brand, index) => {
                  const brandColor = brand.brandColour || getBrandColor(brand);
                  const brandAvatar = getBrandAvatar(brand);
                  const displayName = getBrandDisplay(brand);
                  const isSelected = selectedBrandId === brand.id;
                  
                  // Debug logging for first brand only to avoid spam
                  if (index === 0) {
                    console.log('🎨 SettingsModal RENDER: selectedBrandId =', selectedBrandId, '(', typeof selectedBrandId, ')');
                    console.log('🎨 SettingsModal RENDER: brands[0].id =', brand.id, '(', typeof brand.id, ')');
                  }
                  // Create a guaranteed unique key using multiple fallbacks
                  const uniqueKey = `brand-${String(brand.id)}-idx${index}-${brand.name?.replace(/\s+/g, '-') || 'unnamed'}`;

                  return (
                    <motion.button
                      key={uniqueKey}
                      onClick={() => {
                        const savedBrandId = localStorage.getItem('selectedBrandId');
                        const savedBrandIdParsed = savedBrandId ? (isNaN(savedBrandId) ? savedBrandId : parseInt(savedBrandId)) : null;
                        if (brand.id !== savedBrandIdParsed) {
                          setHasChanges(true);
                        }
                        setSelectedBrandId(brand.id);
                      }}
                      initial={{ opacity: 0 }}
                      animate={{ 
                        opacity: 1
                      }}
                      transition={{ delay: index * 0.05, duration: 0.3 }}
                      className="flex-shrink-0 flex flex-col items-center gap-2 relative group"
                    >
                      {/* Orb container */}
                      <motion.div 
                        animate={{
                          scale: isSelected ? 1.15 : 1
                        }}
                        whileHover={{ scale: isSelected ? 1.2 : 1.08 }}
                        whileTap={{ scale: 0.95 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className={`rounded-full flex items-center justify-center overflow-hidden ${
                          isSelected 
                            ? 'w-14 h-14 shadow-lg' 
                            : 'w-12 h-12 hover:shadow-lg'
                        }`}
                        style={{
                          backgroundColor: brandColor,
                          border: `1px solid ${isSelected ? '#44C2FD' : 'rgba(255, 255, 255, 0.4)'}`,
                          boxShadow: isSelected 
                            ? `0 0 20px ${brandColor}80, 0 0 0 1px #141D2B, 0 0 0 2px #44C2FD` 
                            : undefined
                        }}
                      >
                        {brandAvatar ? (
                          <img
                            src={brandAvatar}
                            alt={displayName}
                            className="w-full h-full object-contain p-1"
                            onLoad={(e) => {
                              console.log(`✅ Avatar loaded successfully for ${displayName}:`, brandAvatar);
                            }}
                            onError={(e) => {
                              console.error(`❌ Failed to load avatar for ${displayName}:`, brandAvatar);
                              e.target.style.display = 'none';
                              // Show the business icon instead
                              const parent = e.target.parentElement;
                              const icon = document.createElement('span');
                              icon.className = 'material-icons text-white text-xl';
                              icon.textContent = 'business';
                              parent.appendChild(icon);
                            }}
                          />
                        ) : (
                          <span className="material-icons text-white text-xl">business</span>
                        )}
                      </motion.div>
                      
                      {/* Selection check */}
                      {isSelected && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute top-0 right-0 w-5 h-5 bg-[#44C2FD] rounded-full flex items-center justify-center border-2 border-[#141D2B]"
                        >
                          <span className="material-icons text-white text-xs">check</span>
                        </motion.div>
                      )}
                      
                      {/* Brand name under orb */}
                      <motion.span 
                        animate={{
                          opacity: isSelected ? 1 : 0.5,
                          scale: isSelected ? 1.1 : 1
                        }}
                        transition={{ duration: 0.3 }}
                        className={`text-xs text-center max-w-[70px] line-clamp-2 transition-all duration-300 ${
                          isSelected ? 'text-white font-semibold' : 'text-gray-400 font-normal'
                        }`}
                        style={{
                          textShadow: isSelected ? '0 0 10px rgba(68, 194, 253, 0.5)' : 'none'
                        }}
                      >
                        {displayName}
                      </motion.span>
                    </motion.button>
                  );
                })}
              </div>

              {/* Right scroll button */}
              {brands.length > 6 && (
                <button
                  onClick={() => scrollBrandCarousel('right')}
                  className="absolute -right-[19px] sm:-right-[27px] top-1/2 -translate-y-1/2 z-10 w-8 h-8 sm:w-10 sm:h-10 bg-[#1C2837]/95 hover:bg-[#44C2FD]/20 border border-gray-700 hover:border-[#44C2FD] rounded-full flex items-center justify-center transition-all duration-200 shadow-lg"
                >
                  <span className="material-icons text-sm sm:text-base text-gray-400 hover:text-[#44C2FD]">chevron_right</span>
                </button>
              )}
            </div>



            <style>{`
              .scrollbar-hide::-webkit-scrollbar {
                display: none;
              }
            `}</style>
          </div>

          {/* Footer with Enhanced Launch Button or Sign Out */}
          <div className="p-4 border-t border-gray-700/50 bg-gradient-to-t from-[#0A0F1A] to-[#141D2B]">
            <div className="flex justify-center gap-4">
              {loading ? (
                // Loading: Show disabled placeholder
                <div className="px-10 py-3 rounded-lg font-semibold text-base uppercase flex items-center gap-2 bg-gray-700/50 text-gray-500 cursor-wait" style={{ letterSpacing: '0.05em' }}>
                  <div className="inline-block animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-gray-400"></div>
                  <span>LOADING...</span>
                </div>
              ) : configs.length === 0 ? (
                // No stories: Show only Sign Out button
                <motion.button
                  onClick={handleSignOut}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-10 py-3 rounded-lg font-semibold text-base transition-all duration-300 uppercase flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white shadow-lg hover:shadow-red-600/50"
                  style={{ letterSpacing: '0.05em' }}
                >
                  <span className="material-icons text-xl">logout</span>
                  <span>SIGN OUT</span>
                </motion.button>
              ) : (
                // Stories available: Show Launch button
                <motion.button
                  onClick={handleLoad}
                  disabled={!canLoad}
                  whileHover={canLoad ? { scale: 1.02 } : {}}
                  whileTap={canLoad ? { scale: 0.98 } : {}}
                  className={`px-10 py-3 rounded-lg font-semibold text-base transition-all duration-300 uppercase flex items-center gap-2 relative overflow-hidden group ${
                    canLoad
                      ? 'bg-gradient-to-r from-[#44C2FD] to-[#23A6D5] hover:from-[#3AB0E8] hover:to-[#1E92BA] text-white shadow-lg hover:shadow-[#44C2FD]/50'
                      : 'bg-gray-700/50 text-gray-500 cursor-not-allowed'
                  }`}
                  style={{ letterSpacing: '0.05em' }}
                >
                  {applying ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                      <span>LOADING...</span>
                    </>
                  ) : (
                    <>
                      <span className="relative z-10">LAUNCH</span>
                      {canLoad && (
                        <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                      )}
                    </>
                  )}
                </motion.button>
              )}
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute top-24 left-1/2 -translate-x-1/2 max-w-md bg-red-500/10 border-2 border-red-500/50 rounded-lg p-4 text-red-300 text-center z-50"
            >
              <span className="material-icons text-2xl mb-2 block">error_outline</span>
              <p className="font-medium">{error}</p>
            </motion.div>
          )}
          
          {/* Loading Overlay - shows while applying changes */}
          {applying && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute inset-0 bg-[#0A0F1A] z-[60] overflow-hidden"
            >
              {/* Animated gradient background with depth - matching LoadingScreen */}
              <div 
                className="absolute inset-0"
                style={{
                  background: `
                    radial-gradient(circle at 80% 20%, rgba(68, 194, 253, 0.12) 0%, transparent 50%),
                    radial-gradient(circle at 20% 80%, rgba(35, 166, 213, 0.08) 0%, transparent 50%),
                    radial-gradient(circle at 50% 50%, rgba(68, 194, 253, 0.03) 0%, transparent 70%),
                    linear-gradient(180deg, #0A0F1A 0%, #1C2837 100%)
                  `,
                }}
              />
              
              {/* Animated mesh gradient overlay */}
              <div 
                className="absolute inset-0 opacity-30"
                style={{
                  backgroundImage: `
                    repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(68, 194, 253, 0.03) 2px, rgba(68, 194, 253, 0.03) 4px),
                    repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(68, 194, 253, 0.03) 2px, rgba(68, 194, 253, 0.03) 4px)
                  `,
                  backgroundSize: '100px 100px',
                  animation: 'meshMove 20s linear infinite',
                }}
              />

              <style>{`
                @keyframes meshMove {
                  0% { transform: translate(0, 0); }
                  100% { transform: translate(100px, 100px); }
                }
                @keyframes spin {
                  to { transform: rotate(360deg); }
                }
                @keyframes glow {
                  0%, 100% { opacity: 0.5; }
                  50% { opacity: 0.8; }
                }
                @keyframes pulse {
                  0%, 100% { transform: scale(1); opacity: 1; }
                  50% { transform: scale(1.05); opacity: 0.8; }
                }
              `}</style>

              {/* Main content container - centered */}
              <div className="relative h-full flex items-center justify-center px-8">
                <div className="text-center">
                  {/* Labb Logo with glow effect */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    className="flex justify-center mb-12 relative"
                  >
                    <div className="absolute inset-0 blur-2xl bg-[#44C2FD] opacity-20 animate-[glow_4s_ease-in-out_infinite]" />
                    <img 
                      src="/images/cara-white.png" 
                      alt="Cara Logo" 
                      className="h-32 relative z-10 drop-shadow-2xl animate-[pulse_3s_ease-in-out_infinite]"
                      style={{ filter: 'brightness(1.15) drop-shadow(0 0 20px rgba(68, 194, 253, 0.5))' }}
                    />
                  </motion.div>

                  {/* Spinner */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="flex justify-center mb-8"
                  >
                    <div 
                      className="relative"
                      style={{
                        width: '80px',
                        height: '80px',
                      }}
                    >
                      {/* Outer ring */}
                      <div
                        className="absolute inset-0 rounded-full"
                        style={{
                          border: '4px solid rgba(68, 194, 253, 0.1)',
                        }}
                      />
                      {/* Spinning ring */}
                      <div
                        className="absolute inset-0 rounded-full"
                        style={{
                          border: '4px solid transparent',
                          borderTopColor: '#44C2FD',
                          borderRightColor: '#44C2FD',
                          animation: 'spin 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite',
                          boxShadow: '0 0 20px rgba(68, 194, 253, 0.4)',
                        }}
                      />
                      {/* Inner glow */}
                      <div
                        className="absolute inset-2 rounded-full bg-[#44C2FD] opacity-10 animate-[pulse_2s_ease-in-out_infinite]"
                      />
                    </div>
                  </motion.div>

                  {/* Loading message */}
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    className="text-white text-lg font-light tracking-wide"
                    style={{
                      fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif',
                      textShadow: '0 2px 20px rgba(68, 194, 253, 0.2)',
                    }}
                  >
                    Loading configuration...
                  </motion.p>
                </div>
              </div>
            </motion.div>
          )}
          </div>
        </motion.div>
    </motion.div>
  );
}


